import {escapeRegex} from 'directory/widget/regex.js';
import http from 'directory/widget/http.js';
import ko from 'knockout';

class NameResult {
    constructor(data, manager) {
        this.id = ko.observable(data.id);
        this.title = ko.observable(data.title);
        this.firstName = ko.observable(data.first_name);
        this.lastName = ko.observable(data.last_name);
        this.avatar = ko.observable(data.avatar);
        this.locality = ko.observable(data.location);
        this.specialties = ko.observableArray();

        this.displayName = ko.pureComputed(() => {
            const name = `${this.title() ||
                ''} ${this.firstName()} ${this.lastName()}`;
            return name.replace(
                new RegExp(`(${escapeRegex(manager.nameQuery())})`, 'gi'),
                '<span class="highlight">$1</span>',
            );
        });

        this.url = ko.pureComputed(() => {
            const profileUrl = data.profile_url;
            if (manager.source) {
                return `${profileUrl}?source=${manager.source}`;
            } else {
                return profileUrl;
            }
        });

        for (const value of data.specialties) {
            if (typeof manager.specialtiesMap[value] !== 'undefined') {
                this.specialties.push(manager.specialtiesMap[value]);
            }
        }
    }
}

export default class NameSearchManager {
    constructor(searchAttrs, elem) {
        this.searchAttrs = searchAttrs;
        this.loading = ko.observable(false);
        this.nameQuery = ko.observable('').extend({
            'rateLimit': 200,
            'method': 'notifyWhenChangesStop',
        });
        this.results = ko.observableArray();
        this.totalCount = ko.observable(-1);
        this.selectedIndex = ko.observable(-1);
        this.limit = 10;
        this.specialtiesMap = {};

        if (elem) {
            this.source = elem.dataset?.source;
        }

        // Specialties needed to display names in search results
        http.get({
            url: '/api/professionals/v1/specialties/',
        }).then((data) => {
            for (const value of data) {
                this.specialtiesMap[value.id] = value.professional_name;
            }
        });

        this.nameQuery.subscribe(() => this.search(true));

        // Handle scroll state of the search results container
        this.selectedIndex.subscribe((val) => {
            if (elem) {
                const results = elem.getElementsByClassName('results')[0];
                if (val >= 0) {
                    const result = results.getElementsByTagName('li')[val];
                    const resultOffset =
                        result.offsetTop + result.offsetHeight;
                    const resultsOffset =
                        results.offsetHeight + results.scrollTop;
                    if (resultOffset > resultsOffset) {
                        // handle scrolling down
                        results.scrollTop += resultOffset - resultsOffset;
                    } else if (result.offsetTop < results.scrollTop) {
                        // handle scrolling up
                        results.scrollTop = result.offsetTop;
                    }
                } else {
                    results.scrollTop = 0;
                }
            }
        });

        this.select = this.select.bind(this);
    }

    clear() {
        this.results([]);
        this.totalCount(-1);
        this.selectedIndex(-1);
    }

    select(result) {
        window.open(result.url(), '_blank');
    }

    async search(clearResults) {
        if (this._q) {
            this._q.abort();
            this._q = null;
        }
        if (this.nameQuery().length < 3) {
            this.clear();
            return;
        }
        if (clearResults) {
            this.clear();
        }
        this.loading(true);
        const args = {
            ...this.searchAttrs,
            name: this.nameQuery(),
            limit: this.limit,
        };
        this._q = http.get({
            url: '/api/professionals/v1/search/',
            data: args,
        });
        const data = await this._q;
        this.totalCount(data.count);
        this.loading(false);
        for (const value of data.results) {
            this.results.push(new NameResult(value, this));
        }
    }

    keydown(_, event) {
        const key = event.key || event.keyCode || event.which;
        if (!this.results().length) {
            return true;
        }
        if (key === 38 || key === 'Up' || key === 'ArrowUp') {
            this.selectedIndex(Math.max(0, this.selectedIndex() - 1));
            return;
        } else if (key === 40 || key === 'Down' || key === 'ArrowDown') {
            const idx = Math.min(
                this.results().length - 1,
                this.selectedIndex() + 1,
            );
            this.selectedIndex(idx);
            return;
        } else if (key === 27 || key === 'Escape') {
            this.clear();
            return;
        }
        return true;
    }

    keypress(_, event) {
        const key = event.key || event.keyCode || event.which;
        if (key === 13 || key === 'Enter') {
            // ENTER
            // If results box is open and a result is highlighted intercept
            if (this.results().length && this.selectedIndex() > -1) {
                this.select(this.results()[this.selectedIndex()]);
                return;
            }
        }
        return true; // Continue with event
    }
}
